import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const OPTIONS = [
  { key: "WITH_PARTS", label: "Sí", hint: "Si no conocés el costo, dejalo vacío: queda pendiente de confirmación administrativa." },
  { key: "NO_PARTS", label: "No", hint: "Costo de repuestos $0 · se aplica el % operativo del taller." },
  { key: "PENDING", label: "Por definir", hint: "Se guarda con su precio; la ganancia queda pendiente." },
];

export const CustomServiceDialog = ({ open, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [policy, setPolicy] = useState("");
  const [note, setNote] = useState("");
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(""); setPrice(""); setPolicy(""); setNote(""); setCost(""); } }, [open]);
  if (!open) return null;

  const n = Number(String(price).replace(/\$/g, "").replace(/\s/g, "").replace(",", "."));
  const priceOk = String(price).trim() !== "" && Number.isFinite(n) && n >= 0;
  const costRaw = String(cost).trim();
  const c = Number(costRaw.replace(/\$/g, "").replace(/\s/g, "").replace(",", "."));
  const useCost = policy === "WITH_PARTS" && costRaw !== "";
  const costErr = !useCost ? "" : !Number.isFinite(c) || c <= 0
    ? "Ingresá un costo válido mayor que cero, o dejalo vacío."
    : priceOk && c >= n ? "El costo del repuesto debe ser menor que el precio de venta. Revisa los importes." : "";
  const valid = name.trim() && priceOk && policy && !costErr;

  async function submit(e) {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(), unit_price: n, parts_policy: policy,
        parts_note: policy === "WITH_PARTS" ? note.trim() : undefined,
        parts_cost: useCost ? c : undefined,
      });
    }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-3" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 p-4 space-y-4 max-h-[90vh] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]"
        data-testid="custom-service-dialog">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Reparación personalizada</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-slate-600">Nombre del servicio</span>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="input-base w-full" placeholder="Ej.: Reparación de pista HDMI" data-testid="custom-service-name" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-slate-600">Precio de venta (USD)</span>
          <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" className="input-base w-full font-mono" placeholder="45" data-testid="custom-service-price" />
          {String(price).trim() !== "" && !priceOk && <span className="text-[11px] text-red-600">Ingresá un número ≥ 0 (ej.: 45 o 45.50).</span>}
        </label>
        <div className="space-y-1.5">
          <span className="text-xs font-semibold text-slate-600">¿Utilizó repuestos?</span>
          <div className="grid grid-cols-3 gap-2" data-testid="custom-service-parts">
            {OPTIONS.map((o) => (
              <button key={o.key} type="button" onClick={() => setPolicy(o.key)}
                className={`py-2.5 rounded-xl border text-sm font-semibold transition-colors ${policy === o.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                data-testid={`custom-service-parts-${o.key}`}>
                {o.label}
              </button>
            ))}
          </div>
          {policy && <p className="text-[11px] text-slate-500" data-testid="custom-service-parts-hint">{OPTIONS.find((o) => o.key === policy).hint}</p>}
        </div>
        {policy === "WITH_PARTS" && (
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-slate-600">¿Qué repuesto utilizó? (opcional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} className="input-base w-full" placeholder="Ej.: Puerto HDMI" data-testid="custom-service-parts-note" />
          </label>
        )}
        {policy === "WITH_PARTS" && (
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-slate-600">Costo del repuesto (USD) — Opcional</span>
            <input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" className="input-base w-full font-mono" placeholder="Costo total de todos los repuestos" data-testid="custom-service-parts-cost" />
            <span className="block text-[11px] text-slate-500">Este costo es interno. El cliente no lo verá.</span>
            {costErr && <span className="block text-[11px] text-red-600" data-testid="custom-service-parts-cost-error">{costErr}</span>}
          </label>
        )}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancelar</button>
          <button type="submit" disabled={!valid || saving} className="btn-primary flex-1 justify-center disabled:opacity-50" data-testid="custom-service-save">
            {saving ? "Guardando…" : "Agregar"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};
